import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center bg-background-50 overflow-hidden pt-16">
      {/* Decorative blurred circles */}
      <div className="absolute top-20 right-[20%] w-[500px] h-[500px] rounded-full bg-accent-50/60 blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-10 left-[10%] w-[400px] h-[400px] rounded-full bg-primary-50/40 blur-[100px] pointer-events-none"></div>
      <div className="absolute top-[40%] left-[40%] w-[300px] h-[300px] rounded-full bg-secondary-100/50 blur-[100px] pointer-events-none"></div>

      <div className="w-full px-6 md:px-10 flex flex-col lg:flex-row items-center gap-10 lg:gap-20">
        {/* Left - Text */}
        <div className="w-full lg:w-[40%] z-10">
          <h1 className="text-5xl md:text-[56px] font-bold text-foreground-950 leading-[1.1] tracking-tight">
            {t('hero.h1_line1', 'Itaobuy')}
            <br />
            {t('hero.h1_line2', 'Spreadsheet')}
            <br />
            {t('hero.h1_line3', 'System')}
          </h1>
          <p className="mt-6 text-base text-foreground-600 leading-relaxed max-w-lg">
            {t('hero.subtitle')}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-rocket-line mr-2"></i>
              {t('hero.cta_access', 'Access ItaobuySpreadsheet Tool')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-secondary-300 text-foreground-700 font-medium text-sm hover:bg-secondary-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-bar-chart-line mr-2"></i>
              {t('hero.cta_demo', 'View Live Spreadsheet Demo')}
            </a>
          </div>
        </div>

        {/* Right - Bento Grid */}
        <div className="w-full lg:w-[60%] z-10">
          <div className="grid grid-cols-4 gap-3 auto-rows-auto">
            {/* Large card - Shoes - spans 2 cols 2 rows */}
            <div className="col-span-2 row-span-2 rounded-3xl bg-white overflow-hidden relative" style={{ height: '320px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=Vibrant%20multicolor%20athletic%20sneakers%20on%20pure%20white%20studio%20background%20dynamic%20sporty%20product%20photography%20with%20neon%20orange%20and%20electric%20blue%20accents%20bold%20energetic%20modern%20footwear%20shot%20with%20crisp%20studio%20lighting%20high%20contrast%20detailed%20texture%20visible&width=600&height=640&seq=hero-bento-shoes-v2&orientation=portrait"
                alt="Shoes"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Small card - Sunglasses */}
            <div className="col-span-1 row-span-1 rounded-2xl bg-white overflow-hidden" style={{ height: '150px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=Stylish%20black%20wayfarer%20sunglasses%20with%20subtle%20gold%20accents%20on%20pure%20white%20background%20modern%20accessories%20photography%20bright%20crisp%20studio%20lighting%20sharp%20reflections%20on%20lenses%20sophisticated%20clean%20product%20display&width=320&height=300&seq=hero-bento-sunglasses-v2&orientation=portrait"
                alt="Accessories"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Small card - Watch */}
            <div className="col-span-1 row-span-1 rounded-2xl bg-white overflow-hidden" style={{ height: '150px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20silver%20stainless%20steel%20watch%20with%20dark%20navy%20blue%20dial%20on%20pure%20white%20background%20luxury%20timepiece%20photography%20bright%20crisp%20studio%20lighting%20metallic%20reflections%20sophisticated%20modern%20accessories%20product%20display&width=320&height=300&seq=hero-bento-watch-v2&orientation=portrait"
                alt="Watch"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Medium card - Backpack */}
            <div className="col-span-1 row-span-1 rounded-2xl bg-white overflow-hidden" style={{ height: '150px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20olive%20green%20canvas%20backpack%20with%20leather%20straps%20on%20pure%20white%20background%20contemporary%20bag%20photography%20bright%20studio%20lighting%20visible%20fabric%20texture%20and%20brass%20hardware%20details%20casual%20travel%20accessory%20product%20display&width=320&height=300&seq=hero-bento-backpack-v2&orientation=portrait"
                alt="Bags"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Medium card - Headphones */}
            <div className="col-span-1 row-span-1 rounded-2xl bg-white overflow-hidden" style={{ height: '150px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=Sleek%20matte%20black%20wireless%20over%20ear%20headphones%20on%20pure%20white%20background%20modern%20tech%20accessories%20photography%20bright%20crisp%20studio%20lighting%20with%20subtle%20metallic%20accents%20premium%20audio%20product%20display%20minimal%20composition&width=320&height=300&seq=hero-bento-headphones-v2&orientation=portrait"
                alt="Headphones"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Small card - Perfume */}
            <div className="col-span-1 row-span-1 rounded-2xl bg-white overflow-hidden" style={{ height: '150px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=Luxurious%20deep%20amber%20glass%20perfume%20bottle%20with%20geometric%20gold%20cap%20on%20pure%20white%20background%20high%20end%20fragrance%20photography%20soft%20bright%20studio%20lighting%20with%20warm%20golden%20reflections%20sophisticated%20modern%20beauty%20product%20display&width=320&height=300&seq=hero-bento-perfume-v2&orientation=portrait"
                alt="Perfumes"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Small card - Wallet */}
            <div className="col-span-1 row-span-1 rounded-2xl bg-white overflow-hidden" style={{ height: '150px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=Rich%20dark%20brown%20leather%20bifold%20wallet%20open%20on%20pure%20white%20background%20showing%20card%20slots%20modern%20accessories%20photography%20bright%20crisp%20studio%20lighting%20visible%20leather%20grain%20texture%20sophisticated%20minimalist%20product%20display&width=320&height=300&seq=hero-bento-wallet-v2&orientation=portrait"
                alt="Wallet"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Floating label on bottom-right of bento */}
          <div className="mt-3 text-right">
            <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-mouse-line"></i>
              14 product categories curated for you
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}